import { useState, useEffect } from "react";
import { Navigate } from "react-router";
import { useAuth } from "../../hooks/useAuth";
import { apiClient } from "../../api/client";
import {
  getBusinessHours,
  updateBusinessHours,
  getOverrides,
  createOverride,
  deleteOverride,
} from "../../api/availability.api";
import type { BusinessProfile, BusinessHours, AvailabilityOverride } from "@abejon/shared";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

interface HourRow {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isOpen: boolean;
}

function defaultHours(): HourRow[] {
  return Array.from({ length: 7 }, (_, i) => ({
    dayOfWeek: i,
    startTime: "09:00",
    endTime: "17:00",
    isOpen: i >= 1 && i <= 5, // Mon-Fri open by default
  }));
}

export function SchedulePage() {
  const { user, isLoading, isAuthenticated } = useAuth();
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [hours, setHours] = useState<HourRow[]>(defaultHours());
  const [overrides, setOverrides] = useState<AvailabilityOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingHours, setSavingHours] = useState(false);
  const [message, setMessage] = useState("");

  // Override form
  const [overrideDate, setOverrideDate] = useState("");
  const [overrideBlocked, setOverrideBlocked] = useState(true);
  const [overrideStart, setOverrideStart] = useState("09:00");
  const [overrideEnd, setOverrideEnd] = useState("17:00");
  const [savingOverride, setSavingOverride] = useState(false);

  useEffect(() => {
    if (!user || user.accountType !== "business") return;
    let cancelled = false;

    async function load() {
      try {
        const data = await apiClient<{ businesses: BusinessProfile[] }>(
          "/businesses?mine=true"
        ).catch(() => null);

        if (cancelled || !data?.businesses?.[0]) {
          if (!cancelled) setLoading(false);
          return;
        }

        const bid = data.businesses[0].id;
        setBusinessId(bid);

        const [hoursData, overridesData] = await Promise.all([
          getBusinessHours(bid),
          getOverrides(bid),
        ]);

        if (cancelled) return;

        if (hoursData.hours.length > 0) {
          setHours(
            hoursData.hours.map((h) => ({
              dayOfWeek: h.dayOfWeek,
              startTime: h.startTime,
              endTime: h.endTime,
              isOpen: h.isOpen,
            }))
          );
        }
        setOverrides(overridesData.overrides);
      } catch {
        // Failed to load
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [user]);

  if (isLoading || loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-gray-900" />
      </div>
    );
  }

  if (!isAuthenticated || user?.accountType !== "business") {
    return <Navigate to="/" replace />;
  }

  if (!businessId) {
    return (
      <div className="py-16 text-center">
        <h1 className="text-xl font-bold text-gray-900">No Business Profile</h1>
        <p className="mt-2 text-sm text-gray-500">
          Create a business profile first in Settings.
        </p>
      </div>
    );
  }

  function updateHour(dayOfWeek: number, field: keyof HourRow, value: string | boolean) {
    setHours((prev) =>
      prev.map((h) =>
        h.dayOfWeek === dayOfWeek ? { ...h, [field]: value } : h
      )
    );
  }

  async function handleSaveHours(e: React.FormEvent) {
    e.preventDefault();
    setSavingHours(true);
    setMessage("");

    try {
      await updateBusinessHours(businessId!, hours);
      setMessage("Hours saved");
    } catch {
      setMessage("Failed to save hours");
    } finally {
      setSavingHours(false);
    }
  }

  async function handleAddOverride(e: React.FormEvent) {
    e.preventDefault();
    if (!overrideDate) return;
    setSavingOverride(true);
    setMessage("");

    try {
      const { override } = await createOverride(businessId!, {
        date: overrideDate,
        isBlocked: overrideBlocked,
        ...(!overrideBlocked ? { startTime: overrideStart, endTime: overrideEnd } : {}),
      });

      setOverrides((prev) => {
        // Replace if same date exists, otherwise append
        const filtered = prev.filter((o) => o.date !== override.date);
        return [...filtered, override].sort((a, b) => a.date.localeCompare(b.date));
      });
      setOverrideDate("");
      setMessage("Override added");
    } catch {
      setMessage("Failed to add override");
    } finally {
      setSavingOverride(false);
    }
  }

  async function handleDeleteOverride(override: AvailabilityOverride) {
    try {
      await deleteOverride(businessId!, override.id);
      setOverrides((prev) => prev.filter((o) => o.id !== override.id));
      setMessage("Override removed");
    } catch {
      setMessage("Failed to remove override");
    }
  }

  return (
    <div className="mx-auto max-w-lg py-8">
      <h1 className="mb-6 text-2xl font-bold">Schedule</h1>

      {message && (
        <p className={`mb-4 text-sm ${message.includes("Failed") ? "text-red-600" : "text-green-600"}`}>
          {message}
        </p>
      )}

      {/* Weekly Hours */}
      <form onSubmit={handleSaveHours} className="mb-8">
        <h2 className="mb-3 text-lg font-semibold">Weekly Hours</h2>
        <div className="space-y-3">
          {hours
            .sort((a, b) => a.dayOfWeek - b.dayOfWeek)
            .map((h) => (
              <div key={h.dayOfWeek} className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 p-3 sm:gap-3">
                <label className="flex w-20 shrink-0 items-center gap-2 sm:w-24">
                  <input
                    type="checkbox"
                    checked={h.isOpen}
                    onChange={(e) => updateHour(h.dayOfWeek, "isOpen", e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-sm font-medium">{DAY_NAMES[h.dayOfWeek].slice(0, 3)}</span>
                </label>
                {h.isOpen ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="time"
                      value={h.startTime}
                      onChange={(e) => updateHour(h.dayOfWeek, "startTime", e.target.value)}
                      className="w-[7rem] rounded-md border border-gray-300 px-2 py-1 text-sm"
                    />
                    <span className="text-sm text-gray-400">to</span>
                    <input
                      type="time"
                      value={h.endTime}
                      onChange={(e) => updateHour(h.dayOfWeek, "endTime", e.target.value)}
                      className="w-[7rem] rounded-md border border-gray-300 px-2 py-1 text-sm"
                    />
                  </div>
                ) : (
                  <span className="text-sm text-gray-400">Closed</span>
                )}
              </div>
            ))}
        </div>
        <button
          type="submit"
          disabled={savingHours}
          className="mt-4 w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
        >
          {savingHours ? "Saving..." : "Save Hours"}
        </button>
      </form>

      {/* Date Overrides */}
      <div>
        <h2 className="mb-3 text-lg font-semibold">Date Overrides</h2>
        <p className="mb-3 text-sm text-gray-500">
          Block specific dates (vacation) or set custom hours for a day.
        </p>

        <form onSubmit={handleAddOverride} className="mb-4 space-y-3 rounded-lg border border-gray-200 p-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Date</label>
            <input
              type="date"
              value={overrideDate}
              onChange={(e) => setOverrideDate(e.target.value)}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Type</label>
            <select
              value={overrideBlocked ? "blocked" : "custom"}
              onChange={(e) => setOverrideBlocked(e.target.value === "blocked")}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
            >
              <option value="blocked">Day Off (Blocked)</option>
              <option value="custom">Custom Hours</option>
            </select>
          </div>

          {!overrideBlocked && (
            <div className="flex items-center gap-2">
              <input
                type="time"
                value={overrideStart}
                onChange={(e) => setOverrideStart(e.target.value)}
                className="flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm"
              />
              <span className="text-sm text-gray-400">to</span>
              <input
                type="time"
                value={overrideEnd}
                onChange={(e) => setOverrideEnd(e.target.value)}
                className="flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={savingOverride}
            className="w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
          >
            {savingOverride ? "Adding..." : "Add Override"}
          </button>
        </form>

        {overrides.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-400">No date overrides set.</p>
        ) : (
          <div className="space-y-2">
            {overrides.map((o) => (
              <div key={o.id} className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
                <div>
                  <span className="text-sm font-medium">{o.date}</span>
                  {o.isBlocked ? (
                    <span className="ml-2 text-xs text-red-500">Blocked</span>
                  ) : (
                    <span className="ml-2 text-xs text-gray-500">
                      {o.startTime} - {o.endTime}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleDeleteOverride(o)}
                  className="text-xs font-medium text-red-500 hover:text-red-700"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
